import type { Language, Locale } from '@/config/locales';

export interface LoginPageTranslation {
  title: string;
  subtitle: string;
  emailLabel: string;
  emailPlaceholder: string;
  passwordLabel: string;
  passwordPlaceholder: string;
  loginButton: string;
  forgotPassword: string;
  noAccount: string;
  signUp: string;
  orContinueWith: string;
  benefits: {
    title: string;
    items: string[];
  };
}

// #512 — Locale 단일 출처 정합.
export type LoginPageTranslations =
  & Record<Language, LoginPageTranslation>
  & Record<Exclude<Locale, Language>, LoginPageTranslation>;

export const loginPageTranslations: LoginPageTranslations = {
  ko: {
    title: '로그인',
    subtitle: 'OLULO에서 특별한 혜택을 누려보세요',
    emailLabel: '이메일',
    emailPlaceholder: '이메일을 입력하세요',
    passwordLabel: '비밀번호',
    passwordPlaceholder: '비밀번호를 입력하세요',
    loginButton: '로그인',
    forgotPassword: '비밀번호를 잊으셨나요?',
    noAccount: '계정이 없으신가요?',
    signUp: '회원가입',
    orContinueWith: '또는 다음으로 계속',
    benefits: {
      title: '로그인 혜택',
      items: ['🎁 첫 주문 15% 할인', '⚡ 빠른 주문 및 결제', '📱 개인화된 메뉴 추천', '🎯 맞춤형 쿠폰 및 이벤트'],
    },
  },
  es: {
    title: 'Iniciar Sesión',
    subtitle: 'Disfruta de beneficios especiales en OLULO',
    emailLabel: 'Correo electrónico',
    emailPlaceholder: 'Ingresa tu correo electrónico',
    passwordLabel: 'Contraseña',
    passwordPlaceholder: 'Ingresa tu contraseña',
    loginButton: 'Iniciar Sesión',
    forgotPassword: '¿Olvidaste tu contraseña?',
    noAccount: '¿No tienes cuenta?',
    signUp: 'Registrarse',
    orContinueWith: 'O continúa con',
    benefits: {
      title: 'Beneficios de Login',
      items: ['🎁 15% de descuento en primer pedido', '⚡ Pedidos y pagos rápidos', '📱 Recomendaciones personalizadas', '🎯 Cupones y eventos personalizados'],
    },
  },
  en: {
    title: 'Login',
    subtitle: 'Enjoy special benefits at OLULO',
    emailLabel: 'Email',
    emailPlaceholder: 'Enter your email',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Enter your password',
    loginButton: 'Login',
    forgotPassword: 'Forgot your password?',
    noAccount: "Don't have an account?",
    signUp: 'Sign Up',
    orContinueWith: 'Or continue with',
    benefits: {
      title: 'Login Benefits',
      items: ['🎁 15% off first order', '⚡ Fast ordering and payment', '📱 Personalized menu recommendations', '🎯 Customized coupons and events'],
    },
  },
  pt: {
    title: 'Entrar',
    subtitle: 'Aproveite benefícios especiais na OLULO',
    emailLabel: 'E-mail',
    emailPlaceholder: 'Digite seu e-mail',
    passwordLabel: 'Senha',
    passwordPlaceholder: 'Digite sua senha',
    loginButton: 'Entrar',
    forgotPassword: 'Esqueceu sua senha?',
    noAccount: 'Não tem uma conta?',
    signUp: 'Cadastrar-se',
    orContinueWith: 'Ou continue com',
    benefits: {
      title: 'Benefícios do Login',
      items: ['🎁 15% de desconto no primeiro pedido', '⚡ Pedidos e pagamentos rápidos', '📱 Recomendações personalizadas', '🎯 Cupons e eventos personalizados'],
    },
  },
  fr: {
    title: 'Connexion',
    subtitle: 'Profitez d’avantages spéciaux chez OLULO',
    emailLabel: 'E-mail',
    emailPlaceholder: 'Saisissez votre e-mail',
    passwordLabel: 'Mot de passe',
    passwordPlaceholder: 'Saisissez votre mot de passe',
    loginButton: 'Se connecter',
    forgotPassword: 'Mot de passe oublié ?',
    noAccount: 'Vous n’avez pas de compte ?',
    signUp: 'S’inscrire',
    orContinueWith: 'Ou continuer avec',
    benefits: {
      title: 'Avantages de la connexion',
      items: ['🎁 15 % de réduction sur la première commande', '⚡ Commandes et paiements rapides', '📱 Recommandations de menu personnalisées', '🎯 Coupons et événements personnalisés'],
    },
  },
  zh: {
    title: '登录',
    subtitle: '在 OLULO 享受特别优惠',
    emailLabel: '电子邮件',
    emailPlaceholder: '请输入您的电子邮件',
    passwordLabel: '密码',
    passwordPlaceholder: '请输入您的密码',
    loginButton: '登录',
    forgotPassword: '忘记密码？',
    noAccount: '没有账户？',
    signUp: '注册',
    orContinueWith: '或继续使用',
    benefits: {
      title: '登录优惠',
      items: ['🎁 首单 15% 折扣', '⚡ 快速下单和支付', '📱 个性化菜单推荐', '🎯 定制优惠券和活动'],
    },
  },
  ja: {
    title: 'ログイン',
    subtitle: 'OLULO で特別な特典をお楽しみください',
    emailLabel: 'メールアドレス',
    emailPlaceholder: 'メールアドレスを入力してください',
    passwordLabel: 'パスワード',
    passwordPlaceholder: 'パスワードを入力してください',
    loginButton: 'ログイン',
    forgotPassword: 'パスワードをお忘れですか？',
    noAccount: 'アカウントをお持ちでないですか？',
    signUp: '新規登録',
    orContinueWith: 'またはこちらで続行',
    benefits: {
      title: 'ログイン特典',
      items: ['🎁 初回注文 15% オフ', '⚡ 素早い注文と支払い', '📱 パーソナライズされたメニュー提案', '🎯 カスタマイズされたクーポンとイベント'],
    },
  },
  hi: {
    title: 'लॉग इन',
    subtitle: 'OLULO में विशेष लाभ का आनंद लें',
    emailLabel: 'ईमेल',
    emailPlaceholder: 'अपना ईमेल दर्ज करें',
    passwordLabel: 'पासवर्ड',
    passwordPlaceholder: 'अपना पासवर्ड दर्ज करें',
    loginButton: 'लॉग इन',
    forgotPassword: 'पासवर्ड भूल गए?',
    noAccount: 'खाता नहीं है?',
    signUp: 'साइन अप',
    orContinueWith: 'या इसके साथ जारी रखें',
    benefits: {
      title: 'लॉगिन लाभ',
      items: ['🎁 पहले ऑर्डर पर 15% की छूट', '⚡ तेज़ ऑर्डर और भुगतान', '📱 व्यक्तिगत मेनू सिफारिशें', '🎯 अनुकूलित कूपन और इवेंट'],
    },
  },
  de: {
    title: 'Anmelden',
    subtitle: 'Genießen Sie besondere Vorteile bei OLULO',
    emailLabel: 'E-Mail',
    emailPlaceholder: 'Geben Sie Ihre E-Mail ein',
    passwordLabel: 'Passwort',
    passwordPlaceholder: 'Geben Sie Ihr Passwort ein',
    loginButton: 'Anmelden',
    forgotPassword: 'Passwort vergessen?',
    noAccount: 'Noch kein Konto?',
    signUp: 'Registrieren',
    orContinueWith: 'Oder weiter mit',
    benefits: {
      title: 'Anmeldevorteile',
      items: ['🎁 15% Rabatt auf erste Bestellung', '⚡ Schnelle Bestellungen und Zahlungen', '📱 Personalisierte Menüempfehlungen', '🎯 Maßgeschneiderte Gutscheine und Events'],
    },
  },
  it: {
    title: 'Accedi',
    subtitle: 'Goditi vantaggi speciali su OLULO',
    emailLabel: 'E-mail',
    emailPlaceholder: 'Inserisci la tua e-mail',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Inserisci la tua password',
    loginButton: 'Accedi',
    forgotPassword: 'Hai dimenticato la password?',
    noAccount: 'Non hai un account?',
    signUp: 'Registrati',
    orContinueWith: 'Oppure continua con',
    benefits: {
      title: 'Vantaggi del login',
      items: ['🎁 15% di sconto sul primo ordine', '⚡ Ordini e pagamenti veloci', '📱 Raccomandazioni di menu personalizzate', '🎯 Coupon ed eventi personalizzati'],
    },
  },
  ru: {
    title: 'Войти',
    subtitle: 'Наслаждайтесь особыми преимуществами в OLULO',
    emailLabel: 'Электронная почта',
    emailPlaceholder: 'Введите вашу электронную почту',
    passwordLabel: 'Пароль',
    passwordPlaceholder: 'Введите ваш пароль',
    loginButton: 'Войти',
    forgotPassword: 'Забыли пароль?',
    noAccount: 'Нет аккаунта?',
    signUp: 'Зарегистрироваться',
    orContinueWith: 'Или продолжить с',
    benefits: {
      title: 'Преимущества входа',
      items: ['🎁 Скидка 15% на первый заказ', '⚡ Быстрые заказы и оплата', '📱 Персонализированные рекомендации меню', '🎯 Индивидуальные купоны и события'],
    },
  },
  ar: {
    title: 'تسجيل الدخول',
    subtitle: 'استمتع بمزايا خاصة في OLULO',
    emailLabel: 'البريد الإلكتروني',
    emailPlaceholder: 'أدخل بريدك الإلكتروني',
    passwordLabel: 'كلمة المرور',
    passwordPlaceholder: 'أدخل كلمة المرور',
    loginButton: 'تسجيل الدخول',
    forgotPassword: 'هل نسيت كلمة المرور؟',
    noAccount: 'ليس لديك حساب؟',
    signUp: 'إنشاء حساب',
    orContinueWith: 'أو تابع باستخدام',
    benefits: {
      title: 'مزايا تسجيل الدخول',
      items: ['🎁 خصم 15% على الطلب الأول', '⚡ طلبات ومدفوعات سريعة', '📱 توصيات قائمة مخصصة', '🎯 كوبونات وفعاليات مخصصة'],
    },
  },
  vi: {
    title: 'Đăng nhập',
    subtitle: 'Tận hưởng các đặc quyền đặc biệt tại OLULO',
    emailLabel: 'Email',
    emailPlaceholder: 'Nhập email của bạn',
    passwordLabel: 'Mật khẩu',
    passwordPlaceholder: 'Nhập mật khẩu của bạn',
    loginButton: 'Đăng nhập',
    forgotPassword: 'Quên mật khẩu?',
    noAccount: 'Chưa có tài khoản?',
    signUp: 'Đăng ký',
    orContinueWith: 'Hoặc tiếp tục với',
    benefits: {
      title: 'Lợi ích khi đăng nhập',
      items: ['🎁 Giảm 15% đơn hàng đầu tiên', '⚡ Đặt hàng và thanh toán nhanh', '📱 Gợi ý menu cá nhân hóa', '🎯 Mã giảm giá và sự kiện tùy chỉnh'],
    },
  },
  th: {
    title: 'เข้าสู่ระบบ',
    subtitle: 'รับสิทธิประโยชน์พิเศษที่ OLULO',
    emailLabel: 'อีเมล',
    emailPlaceholder: 'กรอกอีเมลของคุณ',
    passwordLabel: 'รหัสผ่าน',
    passwordPlaceholder: 'กรอกรหัสผ่านของคุณ',
    loginButton: 'เข้าสู่ระบบ',
    forgotPassword: 'ลืมรหัสผ่าน?',
    noAccount: 'ยังไม่มีบัญชี?',
    signUp: 'สมัครสมาชิก',
    orContinueWith: 'หรือดำเนินการต่อด้วย',
    benefits: {
      title: 'สิทธิประโยชน์ในการเข้าสู่ระบบ',
      items: ['🎁 ส่วนลด 15% สำหรับคำสั่งซื้อแรก', '⚡ สั่งซื้อและชำระเงินเร็ว', '📱 คำแนะนำเมนูเฉพาะบุคคล', '🎯 คูปองและกิจกรรมที่ปรับแต่ง'],
    },
  },
  nl: {
    title: 'Inloggen',
    subtitle: 'Geniet van speciale voordelen bij OLULO',
    emailLabel: 'E-mail',
    emailPlaceholder: 'Voer uw e-mail in',
    passwordLabel: 'Wachtwoord',
    passwordPlaceholder: 'Voer uw wachtwoord in',
    loginButton: 'Inloggen',
    forgotPassword: 'Wachtwoord vergeten?',
    noAccount: 'Heeft u nog geen account?',
    signUp: 'Aanmelden',
    orContinueWith: 'Of ga door met',
    benefits: {
      title: 'Inlogvoordelen',
      items: ['🎁 15% korting op eerste bestelling', '⚡ Snel bestellen en betalen', '📱 Gepersonaliseerde menu-aanbevelingen', '🎯 Aangepaste coupons en evenementen'],
    },
  },
};

// Terms and Privacy content translations
export interface LegalContentTranslation {
  title: string;
  lastUpdated: string;
}

// #512 — Locale 단일 출처 정합.
export type LegalContentTranslations =
  & Record<Language, LegalContentTranslation>
  & Record<Exclude<Locale, Language>, LegalContentTranslation>;

export const termsContent: LegalContentTranslations = {
  ko: { title: '서비스 이용약관', lastUpdated: '최종 업데이트: 2022년 6월 10일' },
  es: { title: 'Términos y Condiciones', lastUpdated: 'Última actualización: 10 de junio de 2022' },
  en: { title: 'Terms and Conditions', lastUpdated: 'Last updated: June 10, 2022' },
  pt: { title: 'Termos e Condições', lastUpdated: 'Última atualização: 10 de junho de 2022' },
  fr: { title: 'Conditions générales', lastUpdated: 'Dernière mise à jour : 10 juin 2022' },
  zh: { title: '服务条款', lastUpdated: '最后更新：2022年6月10日' },
  ja: { title: '利用規約', lastUpdated: '最終更新：2022年6月10日' },
  hi: { title: 'सेवा की शर्तें', lastUpdated: 'अंतिम अपडेट: 10 जून 2022' },
  de: { title: 'Allgemeine Geschäftsbedingungen', lastUpdated: 'Zuletzt aktualisiert: 10. Juni 2022' },
  it: { title: 'Termini e Condizioni', lastUpdated: 'Ultimo aggiornamento: 10 giugno 2022' },
  ru: { title: 'Условия использования', lastUpdated: 'Последнее обновление: 10 июня 2022 г.' },
  ar: { title: 'الشروط والأحكام', lastUpdated: 'آخر تحديث: 10 يونيو 2022' },
  vi: { title: 'Điều khoản và Điều kiện', lastUpdated: 'Cập nhật lần cuối: 10 tháng 6 năm 2022' },
  th: { title: 'ข้อกำหนดและเงื่อนไข', lastUpdated: 'อัปเดตล่าสุด: 10 มิถุนายน 2022' },
  nl: { title: 'Algemene Voorwaarden', lastUpdated: 'Laatst bijgewerkt: 10 juni 2022' },
};

// Phone Authentication 로그인 화면 번역 (#392 Stage 4a)
// LoginPage / AgreementSection / SmsAuthButton 의 하드코딩 영어 키 → es-MX 정합 i18n 일원화.
export interface LoginPhoneAuthTranslation {
  welcomeTitle: string;
  phoneIntro: string;
  getCodeViaSms: string;
  getCodeViaWhatsApp: string;
  sendingCode: string;
  agreeAll: string;
  whatsappConsent: string;
  termsOfServiceLink: string;
  privacyPolicyLink: string;
  privacyNotice: string;
  requiredSuffix: string;
  optionalSuffix: string;
  termsAgreePrefix: string;
  termsAgreeSuffix: string;
  privacyAgreePrefix: string;
  privacyAgreeSuffix: string;
  lfpdpppPrimaryLabel: string;
  lfpdpppSecondaryLabel: string;
}

// #512 — Locale 단일 출처 정합.
export type LoginPhoneAuthTranslations =
  & Record<Language, LoginPhoneAuthTranslation>
  & Record<Exclude<Locale, Language>, LoginPhoneAuthTranslation>;

export const loginPhoneAuthTranslations: LoginPhoneAuthTranslations = {
  ko: {
    welcomeTitle: '환영합니다!',
    phoneIntro: '휴대폰 번호로 간편하게 시작하세요',
    getCodeViaSms: 'SMS로 인증 코드 받기',
    getCodeViaWhatsApp: 'WhatsApp으로 인증 코드 받기',
    sendingCode: '발송 중...',
    agreeAll: '전체 동의',
    whatsappConsent: 'WhatsApp으로 인증 코드를 받는 것에 동의합니다',
    termsOfServiceLink: '서비스 약관',
    privacyPolicyLink: '개인정보 처리방침',
    privacyNotice: '개인정보 고지',
    requiredSuffix: '(필수)',
    optionalSuffix: '(선택)',
    termsAgreePrefix: '',
    termsAgreeSuffix: '에 동의합니다',
    privacyAgreePrefix: '',
    privacyAgreeSuffix: '에 동의합니다',
    lfpdpppPrimaryLabel: '서비스 운영 / 결제 처리 / ARCO 권리에 동의합니다',
    lfpdpppSecondaryLabel: '마케팅 / 통계 목적의 데이터 활용에 동의합니다',
  },
  es: {
    welcomeTitle: '¡Bienvenido!',
    phoneIntro: 'Comienza fácilmente con tu número de teléfono',
    getCodeViaSms: 'Recibir código por SMS',
    getCodeViaWhatsApp: 'Recibir código por WhatsApp',
    sendingCode: 'Enviando...',
    agreeAll: 'Aceptar todo',
    whatsappConsent: 'Acepto recibir mi código de verificación por WhatsApp',
    termsOfServiceLink: 'Términos del Servicio',
    privacyPolicyLink: 'Política de Privacidad',
    privacyNotice: 'Aviso de Privacidad',
    requiredSuffix: '(Obligatorio)',
    optionalSuffix: '(Opcional)',
    termsAgreePrefix: 'Acepto los ',
    termsAgreeSuffix: '',
    privacyAgreePrefix: 'Acepto la ',
    privacyAgreeSuffix: '',
    lfpdpppPrimaryLabel: 'Acepto el tratamiento de datos personales conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP) — operación del servicio, pagos y derechos ARCO',
    lfpdpppSecondaryLabel: 'Acepto el uso de datos con fines de marketing y estadísticas conforme a la LFPDPPP',
  },
  en: {
    welcomeTitle: 'Welcome!',
    phoneIntro: 'Get started easily with your phone number',
    getCodeViaSms: 'Get code via SMS',
    getCodeViaWhatsApp: 'Get code via WhatsApp',
    sendingCode: 'Sending...',
    agreeAll: 'Agree to all',
    whatsappConsent: 'I agree to receive my verification code via WhatsApp',
    termsOfServiceLink: 'Terms of Service',
    privacyPolicyLink: 'Privacy Policy',
    privacyNotice: 'Privacy Notice',
    requiredSuffix: '(Required)',
    optionalSuffix: '(Optional)',
    termsAgreePrefix: 'I agree to the ',
    termsAgreeSuffix: '',
    privacyAgreePrefix: 'I agree to the ',
    privacyAgreeSuffix: '',
    lfpdpppPrimaryLabel: 'I consent to data processing for service operation, payments, and ARCO rights',
    lfpdpppSecondaryLabel: 'I consent to data use for marketing and statistics',
  },
  pt: {
    welcomeTitle: 'Bem-vindo!',
    phoneIntro: 'Comece facilmente com seu número de telefone',
    getCodeViaSms: 'Receber código por SMS',
    getCodeViaWhatsApp: 'Receber código pelo WhatsApp',
    sendingCode: 'Enviando...',
    agreeAll: 'Aceitar tudo',
    whatsappConsent: 'Concordo em receber meu código de verificação pelo WhatsApp',
    termsOfServiceLink: 'Termos de Serviço',
    privacyPolicyLink: 'Política de Privacidade',
    privacyNotice: 'Aviso de Privacidade',
    requiredSuffix: '(Obrigatório)',
    optionalSuffix: '(Opcional)',
    termsAgreePrefix: 'Concordo com os ',
    termsAgreeSuffix: '',
    privacyAgreePrefix: 'Concordo com a ',
    privacyAgreeSuffix: '',
    lfpdpppPrimaryLabel: 'Consinto o tratamento de dados para operação do serviço, pagamentos e direitos ARCO',
    lfpdpppSecondaryLabel: 'Consinto o uso de dados para fins de marketing e estatísticas',
  },
  fr: {
    welcomeTitle: 'Bienvenue !',
    phoneIntro: 'Commencez facilement avec votre numéro de téléphone',
    getCodeViaSms: 'Recevoir le code par SMS',
    getCodeViaWhatsApp: 'Recevoir le code via WhatsApp',
    sendingCode: 'Envoi...',
    agreeAll: 'Tout accepter',
    whatsappConsent: 'J’accepte de recevoir mon code de vérification via WhatsApp',
    termsOfServiceLink: 'Conditions d’utilisation',
    privacyPolicyLink: 'Politique de confidentialité',
    privacyNotice: 'Avis de confidentialité',
    requiredSuffix: '(Obligatoire)',
    optionalSuffix: '(Facultatif)',
    termsAgreePrefix: 'J’accepte les ',
    termsAgreeSuffix: '',
    privacyAgreePrefix: 'J’accepte la ',
    privacyAgreeSuffix: '',
    lfpdpppPrimaryLabel: 'J’accepte le traitement des données pour l’exploitation du service, les paiements et les droits ARCO',
    lfpdpppSecondaryLabel: 'J’accepte l’utilisation des données à des fins de marketing et de statistiques',
  },
  zh: {
    welcomeTitle: '欢迎！',
    phoneIntro: '使用您的电话号码轻松开始',
    getCodeViaSms: '通过短信获取验证码',
    getCodeViaWhatsApp: '通过 WhatsApp 获取验证码',
    sendingCode: '发送中...',
    agreeAll: '全部同意',
    whatsappConsent: '我同意通过 WhatsApp 接收验证码',
    termsOfServiceLink: '服务条款',
    privacyPolicyLink: '隐私政策',
    privacyNotice: '隐私通知',
    requiredSuffix: '(必填)',
    optionalSuffix: '(选填)',
    termsAgreePrefix: '我同意 ',
    termsAgreeSuffix: '',
    privacyAgreePrefix: '我同意 ',
    privacyAgreeSuffix: '',
    lfpdpppPrimaryLabel: '我同意为服务运营、支付和 ARCO 权利处理数据',
    lfpdpppSecondaryLabel: '我同意将数据用于营销和统计目的',
  },
  ja: {
    welcomeTitle: 'ようこそ！',
    phoneIntro: '電話番号で簡単に始めましょう',
    getCodeViaSms: 'SMS で認証コードを受け取る',
    getCodeViaWhatsApp: 'WhatsApp で認証コードを受け取る',
    sendingCode: '送信中...',
    agreeAll: 'すべて同意',
    whatsappConsent: 'WhatsApp で認証コードを受け取ることに同意します',
    termsOfServiceLink: '利用規約',
    privacyPolicyLink: 'プライバシーポリシー',
    privacyNotice: 'プライバシー通知',
    requiredSuffix: '(必須)',
    optionalSuffix: '(任意)',
    termsAgreePrefix: '',
    termsAgreeSuffix: 'に同意します',
    privacyAgreePrefix: '',
    privacyAgreeSuffix: 'に同意します',
    lfpdpppPrimaryLabel: 'サービス運営、支払い、ARCO 権利のためのデータ処理に同意します',
    lfpdpppSecondaryLabel: 'マーケティングおよび統計目的でのデータ使用に同意します',
  },
  hi: {
    welcomeTitle: 'स्वागत है!',
    phoneIntro: 'अपने फोन नंबर के साथ आसानी से शुरू करें',
    getCodeViaSms: 'SMS द्वारा कोड प्राप्त करें',
    getCodeViaWhatsApp: 'WhatsApp द्वारा कोड प्राप्त करें',
    sendingCode: 'भेज रहा है...',
    agreeAll: 'सभी से सहमत',
    whatsappConsent: 'मैं WhatsApp के माध्यम से अपना सत्यापन कोड प्राप्त करने के लिए सहमत हूं',
    termsOfServiceLink: 'सेवा की शर्तें',
    privacyPolicyLink: 'गोपनीयता नीति',
    privacyNotice: 'गोपनीयता सूचना',
    requiredSuffix: '(आवश्यक)',
    optionalSuffix: '(वैकल्पिक)',
    termsAgreePrefix: 'मैं ',
    termsAgreeSuffix: ' से सहमत हूं',
    privacyAgreePrefix: 'मैं ',
    privacyAgreeSuffix: ' से सहमत हूं',
    lfpdpppPrimaryLabel: 'मैं सेवा संचालन, भुगतान और ARCO अधिकारों के लिए डेटा प्रसंस्करण के लिए सहमत हूं',
    lfpdpppSecondaryLabel: 'मैं विपणन और सांख्यिकी उद्देश्यों के लिए डेटा उपयोग के लिए सहमत हूं',
  },
  de: {
    welcomeTitle: 'Willkommen!',
    phoneIntro: 'Beginnen Sie einfach mit Ihrer Telefonnummer',
    getCodeViaSms: 'Code per SMS erhalten',
    getCodeViaWhatsApp: 'Code per WhatsApp erhalten',
    sendingCode: 'Senden...',
    agreeAll: 'Allen zustimmen',
    whatsappConsent: 'Ich stimme zu, meinen Verifizierungscode über WhatsApp zu erhalten',
    termsOfServiceLink: 'Nutzungsbedingungen',
    privacyPolicyLink: 'Datenschutzrichtlinie',
    privacyNotice: 'Datenschutzhinweis',
    requiredSuffix: '(Erforderlich)',
    optionalSuffix: '(Optional)',
    termsAgreePrefix: 'Ich stimme den ',
    termsAgreeSuffix: ' zu',
    privacyAgreePrefix: 'Ich stimme der ',
    privacyAgreeSuffix: ' zu',
    lfpdpppPrimaryLabel: 'Ich stimme der Datenverarbeitung für Servicebetrieb, Zahlungen und ARCO-Rechte zu',
    lfpdpppSecondaryLabel: 'Ich stimme der Datennutzung für Marketing- und Statistikzwecke zu',
  },
  it: {
    welcomeTitle: 'Benvenuto!',
    phoneIntro: 'Inizia facilmente con il tuo numero di telefono',
    getCodeViaSms: 'Ricevi codice via SMS',
    getCodeViaWhatsApp: 'Ricevi codice via WhatsApp',
    sendingCode: 'Invio in corso...',
    agreeAll: 'Accetta tutto',
    whatsappConsent: 'Accetto di ricevere il mio codice di verifica via WhatsApp',
    termsOfServiceLink: 'Termini di servizio',
    privacyPolicyLink: 'Informativa sulla privacy',
    privacyNotice: 'Avviso sulla privacy',
    requiredSuffix: '(Obbligatorio)',
    optionalSuffix: '(Facoltativo)',
    termsAgreePrefix: 'Accetto i ',
    termsAgreeSuffix: '',
    privacyAgreePrefix: 'Accetto l’',
    privacyAgreeSuffix: '',
    lfpdpppPrimaryLabel: 'Acconsento al trattamento dei dati per il funzionamento del servizio, pagamenti e diritti ARCO',
    lfpdpppSecondaryLabel: 'Acconsento all’uso dei dati a fini di marketing e statistiche',
  },
  ru: {
    welcomeTitle: 'Добро пожаловать!',
    phoneIntro: 'Начните легко с номером телефона',
    getCodeViaSms: 'Получить код по SMS',
    getCodeViaWhatsApp: 'Получить код через WhatsApp',
    sendingCode: 'Отправка...',
    agreeAll: 'Согласиться со всем',
    whatsappConsent: 'Я согласен получать код подтверждения через WhatsApp',
    termsOfServiceLink: 'Условия обслуживания',
    privacyPolicyLink: 'Политика конфиденциальности',
    privacyNotice: 'Уведомление о конфиденциальности',
    requiredSuffix: '(Обязательно)',
    optionalSuffix: '(Необязательно)',
    termsAgreePrefix: 'Я согласен с ',
    termsAgreeSuffix: '',
    privacyAgreePrefix: 'Я согласен с ',
    privacyAgreeSuffix: '',
    lfpdpppPrimaryLabel: 'Я согласен на обработку данных для функционирования сервиса, платежей и прав ARCO',
    lfpdpppSecondaryLabel: 'Я согласен на использование данных в маркетинговых и статистических целях',
  },
  ar: {
    welcomeTitle: 'مرحباً!',
    phoneIntro: 'ابدأ بسهولة برقم هاتفك',
    getCodeViaSms: 'احصل على الرمز عبر الرسائل القصيرة',
    getCodeViaWhatsApp: 'احصل على الرمز عبر WhatsApp',
    sendingCode: 'جاري الإرسال...',
    agreeAll: 'الموافقة على الكل',
    whatsappConsent: 'أوافق على استلام رمز التحقق عبر WhatsApp',
    termsOfServiceLink: 'شروط الخدمة',
    privacyPolicyLink: 'سياسة الخصوصية',
    privacyNotice: 'إشعار الخصوصية',
    requiredSuffix: '(مطلوب)',
    optionalSuffix: '(اختياري)',
    termsAgreePrefix: 'أوافق على ',
    termsAgreeSuffix: '',
    privacyAgreePrefix: 'أوافق على ',
    privacyAgreeSuffix: '',
    lfpdpppPrimaryLabel: 'أوافق على معالجة البيانات لتشغيل الخدمة والمدفوعات وحقوق ARCO',
    lfpdpppSecondaryLabel: 'أوافق على استخدام البيانات لأغراض التسويق والإحصاءات',
  },
  vi: {
    welcomeTitle: 'Chào mừng!',
    phoneIntro: 'Bắt đầu dễ dàng với số điện thoại của bạn',
    getCodeViaSms: 'Nhận mã qua SMS',
    getCodeViaWhatsApp: 'Nhận mã qua WhatsApp',
    sendingCode: 'Đang gửi...',
    agreeAll: 'Đồng ý tất cả',
    whatsappConsent: 'Tôi đồng ý nhận mã xác minh qua WhatsApp',
    termsOfServiceLink: 'Điều khoản dịch vụ',
    privacyPolicyLink: 'Chính sách bảo mật',
    privacyNotice: 'Thông báo bảo mật',
    requiredSuffix: '(Bắt buộc)',
    optionalSuffix: '(Tuỳ chọn)',
    termsAgreePrefix: 'Tôi đồng ý với ',
    termsAgreeSuffix: '',
    privacyAgreePrefix: 'Tôi đồng ý với ',
    privacyAgreeSuffix: '',
    lfpdpppPrimaryLabel: 'Tôi đồng ý xử lý dữ liệu cho hoạt động dịch vụ, thanh toán và quyền ARCO',
    lfpdpppSecondaryLabel: 'Tôi đồng ý sử dụng dữ liệu cho mục đích tiếp thị và thống kê',
  },
  th: {
    welcomeTitle: 'ยินดีต้อนรับ!',
    phoneIntro: 'เริ่มต้นใช้งานได้ง่ายด้วยหมายเลขโทรศัพท์ของคุณ',
    getCodeViaSms: 'รับรหัสผ่าน SMS',
    getCodeViaWhatsApp: 'รับรหัสผ่าน WhatsApp',
    sendingCode: 'กำลังส่ง...',
    agreeAll: 'ยอมรับทั้งหมด',
    whatsappConsent: 'ฉันยอมรับการรับรหัสยืนยันผ่าน WhatsApp',
    termsOfServiceLink: 'ข้อกำหนดในการให้บริการ',
    privacyPolicyLink: 'นโยบายความเป็นส่วนตัว',
    privacyNotice: 'ประกาศความเป็นส่วนตัว',
    requiredSuffix: '(จำเป็น)',
    optionalSuffix: '(เลือกได้)',
    termsAgreePrefix: 'ฉันยอมรับ ',
    termsAgreeSuffix: '',
    privacyAgreePrefix: 'ฉันยอมรับ ',
    privacyAgreeSuffix: '',
    lfpdpppPrimaryLabel: 'ฉันยินยอมให้ประมวลผลข้อมูลสำหรับการดำเนินการบริการ การชำระเงิน และสิทธิ์ ARCO',
    lfpdpppSecondaryLabel: 'ฉันยินยอมให้ใช้ข้อมูลเพื่อวัตถุประสงค์ทางการตลาดและสถิติ',
  },
  nl: {
    welcomeTitle: 'Welkom!',
    phoneIntro: 'Begin gemakkelijk met uw telefoonnummer',
    getCodeViaSms: 'Code ontvangen via SMS',
    getCodeViaWhatsApp: 'Code ontvangen via WhatsApp',
    sendingCode: 'Verzenden...',
    agreeAll: 'Alles accepteren',
    whatsappConsent: 'Ik ga ermee akkoord mijn verificatiecode via WhatsApp te ontvangen',
    termsOfServiceLink: 'Servicevoorwaarden',
    privacyPolicyLink: 'Privacybeleid',
    privacyNotice: 'Privacyverklaring',
    requiredSuffix: '(Verplicht)',
    optionalSuffix: '(Optioneel)',
    termsAgreePrefix: 'Ik ga akkoord met de ',
    termsAgreeSuffix: '',
    privacyAgreePrefix: 'Ik ga akkoord met het ',
    privacyAgreeSuffix: '',
    lfpdpppPrimaryLabel: 'Ik stem in met gegevensverwerking voor servicewerking, betalingen en ARCO-rechten',
    lfpdpppSecondaryLabel: 'Ik stem in met gegevensgebruik voor marketing en statistieken',
  },
};

export const privacyContent: LegalContentTranslations = {
  ko: { title: '개인정보 보호정책', lastUpdated: '최종 업데이트: 2022년 5월 24일' },
  es: { title: 'Política de Privacidad', lastUpdated: 'Última actualización: 24 de mayo de 2022' },
  en: { title: 'Privacy Policy', lastUpdated: 'Last updated: May 24, 2022' },
  pt: { title: 'Política de Privacidade', lastUpdated: 'Última atualização: 24 de maio de 2022' },
  fr: { title: 'Politique de confidentialité', lastUpdated: 'Dernière mise à jour : 24 mai 2022' },
  zh: { title: '隐私政策', lastUpdated: '最后更新：2022年5月24日' },
  ja: { title: 'プライバシーポリシー', lastUpdated: '最終更新：2022年5月24日' },
  hi: { title: 'गोपनीयता नीति', lastUpdated: 'अंतिम अपडेट: 24 मई 2022' },
  de: { title: 'Datenschutzrichtlinie', lastUpdated: 'Zuletzt aktualisiert: 24. Mai 2022' },
  it: { title: 'Informativa sulla privacy', lastUpdated: 'Ultimo aggiornamento: 24 maggio 2022' },
  ru: { title: 'Политика конфиденциальности', lastUpdated: 'Последнее обновление: 24 мая 2022 г.' },
  ar: { title: 'سياسة الخصوصية', lastUpdated: 'آخر تحديث: 24 مايو 2022' },
  vi: { title: 'Chính sách bảo mật', lastUpdated: 'Cập nhật lần cuối: 24 tháng 5 năm 2022' },
  th: { title: 'นโยบายความเป็นส่วนตัว', lastUpdated: 'อัปเดตล่าสุด: 24 พฤษภาคม 2022' },
  nl: { title: 'Privacybeleid', lastUpdated: 'Laatst bijgewerkt: 24 mei 2022' },
};
